import React from "react";
import { Box, Sheet, Table, Chip, CircularProgress } from "@mui/joy";
import DarkTypography from "../../common/DarkTypography";
import { EventIcon } from "../../../styles/StyledMUI";
import { colorTokens, hrSheetSx, hrTableSx, textTokens } from "../../../styles/globalStyles";
import { formatDateWithLocale } from "../../../utils/dateTime";
import type { ExcursionStatus, Excursion } from "../../../api/excursions/Excursion.typed";
import ExcursionModal from "../excursionModal/display/ExcursionModal";
import ExcursionCardTooltip from "../excursionModal/display/ExcursionCardTooltip";
import { useExcursionLabels } from "../../excursions/planning/hooks/useExcursionLabels";
import { useExcursionModalUsers } from "../../../contexts/app/customersData";

type CustomerExcursionsTabProps = {
  excursions: Excursion[];
  loading: boolean;
  totalItems: number;
};

const STATUS_CONFIG: Record<
  ExcursionStatus,
  { label: string; color: "success" | "primary" | "danger" }
> = {
  scheduled: { label: "Planifiée", color: "primary" },
  validated: { label: "Validée", color: "success" },
  cancelled: { label: "Annulée", color: "danger" },
};

function formatTime(raw?: string | null): string {
  if (!raw) return "";
  const parts = raw.split(":");

  return parts.length >= 2 ? `${parts[0]}h${parts[1]}` : raw;
}

function formatTimeRange(start?: string | null, end?: string | null): string {
  const startFmt = formatTime(start);
  const endFmt = formatTime(end);

  if (!startFmt) return "";

  return endFmt ? `${startFmt} – ${endFmt}` : startFmt;
}

function renderExcursionRow(exc: Excursion) {
  const status = exc.status ?? "scheduled";
  const cfg = STATUS_CONFIG[status];

  return (
    <tr style={{ cursor: "pointer" }}>
      <td>
        <DarkTypography level="body-sm" sx={{ fontWeight: "bold" }}>
          {formatDateWithLocale(exc.date)}
        </DarkTypography>
      </td>
      <td>
        <DarkTypography level="body-sm" sx={{ whiteSpace: "nowrap" }}>
          {formatTimeRange(exc.start_time, exc.end_time) || "—"}
        </DarkTypography>
      </td>
      <td>
        <DarkTypography
          level="body-sm"
          sx={{
            minWidth: 0,
            maxWidth: { xs: "min(40vw, 200px)", sm: 240 },
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={exc.name}
        >
          {exc.name}
        </DarkTypography>
      </td>
      <td>
        <Chip variant="soft" color={cfg.color} size="sm">
          {cfg.label}
        </Chip>
      </td>
    </tr>
  );
}

export default function CustomerExcursionsTab({
  excursions,
  loading,
  totalItems,
}: CustomerExcursionsTabProps) {
  const [selectedExcursion, setSelectedExcursion] = React.useState<Excursion | null>(null);
  const { getGroupLabels, rawCustomers, getCustomerInProgressTrainingsTooltip } =
    useExcursionLabels(excursions);
  const excursionModalUsers = useExcursionModalUsers();

  if (loading && excursions.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size="md" />
      </Box>
    );
  }

  if (!loading && totalItems === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
          py: 4,
        }}
      >
        <EventIcon sx={{ fontSize: 40, color: colorTokens.text.muted }} />
        <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed }}>
          Aucune sortie pour ce client
        </DarkTypography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: { xs: "95vw", sm: "100%" },
          minWidth: 0,
        }}
      >
        <Sheet sx={{ ...hrSheetSx, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table
            hoverRow
            sx={{
              ...hrTableSx,
              minWidth: { xs: 480, sm: "auto" },
              opacity: loading ? 0.5 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Horaire</th>
                <th>Nom</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {excursions.map((exc) => (
                <ExcursionCardTooltip
                  key={exc.id}
                  excursion={exc}
                  canEdit={false}
                  groupLabels={getGroupLabels(exc)}
                  getCustomerInProgressTrainingsTooltip={getCustomerInProgressTrainingsTooltip}
                  onTooltipClick={() => setSelectedExcursion(exc)}
                >
                  {renderExcursionRow(exc)}
                </ExcursionCardTooltip>
              ))}
            </tbody>
          </Table>
        </Sheet>
      </Box>

      {selectedExcursion && (
        <ExcursionModal
          readonly
          open
          onClose={() => setSelectedExcursion(null)}
          initialExcursion={selectedExcursion}
          preloadedCustomers={rawCustomers}
          preloadedUsers={excursionModalUsers}
        />
      )}
    </>
  );
}
